import type { McpProbeResult } from '@ralph/contracts'
import clsx from 'clsx'
import { Check, KeyRound, Minus, Quote, X } from 'lucide-react'
import { Panel, Spinner } from '@/components/ui/primitives.tsx'
import { useI18n, useT } from '@/i18n/index.ts'
import { STAGE_ORDER, type StageView, stagesFrom } from './system-stages.ts'

/**
 * The six steps, and what each one found. One component, used in both places.
 *
 * It was two — the connect flow had this, and the system's own page had a copy — and they
 * had already drifted: the copy rendered `pending` and `skipped` identically and with no
 * mark at all, so the same 401 looked like a different diagnosis depending on which screen
 * you were standing on. That is worse than either rendering, because a reader who saw both
 * has no way to tell which one is lying.
 *
 * A ladder whose whole purpose is to be believed cannot have two implementations.
 *
 * An ordered list rather than a table: it is read top to bottom, each row depends on the one
 * above, and a table invites the eye to compare columns that have nothing to do with each
 * other. The icon and the word always agree — colour is never the only carrier (AGM-124) —
 * and a step that was not reached is drawn muted with a dash rather than a red cross, which
 * is the difference between "we did not ask" and "the answer was no".
 */

const PENDING_PLACEHOLDER: StageView[] = STAGE_ORDER.map((key) => ({ key, state: 'pending', detail: '' }))

export const StageLadder = ({
  result,
  url,
  checking = false,
  showClaim = false
}: {
  result: McpProbeResult | undefined
  url: string
  /** Draws every step as in-progress while a probe is in flight. */
  checking?: boolean
  /** The connect flow shows the system's claimed identity here; the detail page has its own. */
  showClaim?: boolean
}) => {
  // Every word on the ladder — the six evidence lines and its one counted sentence — is
  // read here and handed to the pure module, rather than imported by it. A module-scope
  // catalogue read freezes the locale for the session with nothing failing (F-3, LOC-012).
  const fmt = useI18n()
  const t = fmt.t.admin.systems
  const stages = result ? stagesFrom(t, fmt, result, url) : PENDING_PLACEHOLDER

  return (
    <Panel className="px-4 py-3.5">
      <h3 className="font-sans text-[14px] font-semibold tracking-[-0.018em]">{t.flow.stagesTitle}</h3>
      <p className="mt-1 text-[12.5px] leading-snug text-fg-muted">{t.flow.stagesLead}</p>

      <ol className="mt-3 space-y-0.5">
        {stages.map((stage) => (
          <li
            key={stage.key}
            className="flex items-start gap-2.5 rounded-tile px-2 py-1.5 odd:bg-fg/[0.02]"
            data-state={stage.state}
          >
            <StageMark state={stage.state} checking={checking} />
            <div className="min-w-0 flex-1">
              <p
                className={clsx(
                  'text-[13px] leading-snug',
                  stage.state === 'pending' && 'text-fg-subtle',
                  stage.state === 'fail' && 'font-semibold text-alert'
                )}
              >
                {t.flow.stage[stage.key]}
              </p>
              {stage.detail !== '' && (
                <p className="mt-0.5 break-words text-[12.5px] leading-relaxed text-fg-muted">{stage.detail}</p>
              )}
              {stage.state === 'pending' && (
                <p className="mt-0.5 text-[12.5px] text-fg-subtle">
                  {checking ? t.flow.stageChecking : t.flow.stagePending}
                </p>
              )}
              {/* A system is not obliged to name itself, and silence there is not a fault.
                  Said in words rather than left as a blank line under a green tick. */}
              {stage.key === 'identity' && stage.state === 'pass' && stage.detail === '' && (
                <p className="mt-0.5 text-[12.5px] text-fg-subtle">{t.flow.noIdentity}</p>
              )}
            </div>
          </li>
        ))}
      </ol>

      {showClaim && result?.ok && <IdentityClaim result={result} />}
    </Panel>
  )
}

const StageMark = ({ state, checking }: { state: StageView['state']; checking: boolean }) => {
  const shared = 'mt-[3px] flex h-4 w-4 shrink-0 items-center justify-center rounded-full'
  if (state === 'pass') {
    return (
      <span className={clsx(shared, 'bg-grounded/15 text-grounded')} aria-hidden>
        <Check size={11} strokeWidth={3} />
      </span>
    )
  }
  if (state === 'fail') {
    return (
      <span className={clsx(shared, 'bg-alert/15 text-alert')} aria-hidden>
        <X size={11} strokeWidth={3} />
      </span>
    )
  }
  // Not a failure: the system answered that it issues its own credentials, which is the next
  // question the flow was going to ask. A red cross here teaches somebody that a working
  // system is broken.
  if (state === 'skipped') {
    return (
      <span className={clsx(shared, 'bg-primary/15 text-primary')} aria-hidden>
        <KeyRound size={10} strokeWidth={2.6} />
      </span>
    )
  }
  return (
    <span className={clsx(shared, 'text-fg-subtle')} aria-hidden>
      {checking ? <Spinner size={11} /> : <Minus size={11} strokeWidth={3} />}
    </span>
  )
}

/**
 * What the system says about itself, drawn as somebody else's words.
 *
 * The same quotation grammar the capability curation panel uses, for the same reason: the
 * failure mode is a plausible-sounding claim being read as something the platform verified.
 * An icon a system supplies is fetched by the browser, not by the API — the platform does
 * not dial a URL a remote party chose, which is the whole point of the endpoint allow-list —
 * and its absence is not an error.
 */
const IdentityClaim = ({ result }: { result: McpProbeResult }) => {
  const t = useT().admin.systems
  const info = result.serverInfo as { name?: string; version?: string; description?: string; websiteUrl?: string }
  if (!info.name && !info.description) return null

  return (
    <blockquote className="mt-3 rounded-r-tile border-l-2 border-rule-strong bg-fg/[0.03] py-2.5 pr-3 pl-3.5">
      <p className="eyebrow flex items-center gap-1.5">
        <Quote size={10} strokeWidth={2.6} aria-hidden />
        {t.flow.claimTitle}
      </p>
      {info.name && (
        <p className="mt-1.5 text-[13px] leading-snug">
          {info.name}
          {info.version && <span className="readout ml-1.5 text-fg-subtle">{info.version}</span>}
        </p>
      )}
      {info.description && <p className="mt-1 text-[12.5px] leading-relaxed text-fg-muted">{info.description}</p>}
      <p className="mt-1.5 text-[12px] leading-snug text-fg-subtle">{t.flow.claimHint}</p>
    </blockquote>
  )
}
